import React, { useState, useEffect, ChangeEvent, FormEvent } from 'react';
import {
	Box,
	Paper,
	Typography,
	TextField,
	Button,
	Alert,
	Link,
	CircularProgress,
	Fade,
	Stack,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Network } from "../util/Network";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useAppSocket } from '../providers/AppSocketContext';
import OfflineScreen from '../components/OfflineScreen';
import { useUI } from '../providers/MoleUIContext';
import { useTranslation } from 'react-i18next';
import { Util } from '../util/Util';
import { readStoredSession } from '../util/sessionStore';
import { normalizeAccess } from '../util/access';
import { getDefaultPathForAccess } from '../util/routeAccess';

interface Props {
	isModal?: boolean;
	setIsModal?: (isModal: boolean) => void;
}

interface LoginFormState {
	username: string;
	password: string;
	error: string;
	success: string;
	warning: string;
}

const Login = ({ isModal, setIsModal }: Props) => {
	const [loading, setLoading] = useState(false);
	const [DOMReady, setDOMReady] = useState(false);
	const { setSession, session, access } = useAuth();
	const { loginModalOpen } = useUI();
	const { connected } = useAppSocket();

	const { t, i18n } = useTranslation();

	const navigate = useNavigate();

	const [formData, setFormData] = useState<LoginFormState>({
		username: '',
		password: '',
		error: '',
		success: '',
		warning: '',
	});

	const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
			error: '',
			success: '',
		}));
	};

	const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!formData.username || !formData.password) {
			setFormData((prev) => ({ ...prev, error: t("common:validation.allFieldsRequired") }));
			return;
		}

		setLoading(true);
		setFormData((prev) => ({ ...prev, error: '', success: '' }));

		try {
			const clientMeta = {
				timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
				language: navigator.language,
				platform: navigator.platform,
				screen: `${window.screen?.width || 0}x${window.screen?.height || 0}`,
			};
			const response = await Network.loginUser({
				username: formData.username,
				password: formData.password,
				clientMeta,
			});

			if (response.status === 200) {
				const access = normalizeAccess(
					response.body?.resources,
					response.body?.postings,
				);
				// setSession persists + broadcasts to every open tab.
				setSession({ ...response.body, access });
				setFormData((prev) => ({ ...prev, success: t("common:login.loginSuccess") }));

				i18n.changeLanguage(response.body.lang || 'en');

				// The login response carries the grants; landing has to read them,
				// since every screen is a grant now.
				const dest = getDefaultPathForAccess({
					access,
				});

				setTimeout(() => {
					if (isModal && setIsModal) setIsModal(false);
					else navigate(dest, { replace: true });
				}, 400);
			} else {
				setFormData((prev) => ({
					...prev,
					error: response.body?.message || t("common:login.loginFailed"),
				}));
			}
		} catch {
			setFormData((prev) => ({
				...prev,
				error: t("common:login.networkError"),
			}));
		} finally {
			setLoading(false);
		}
	};

	// Full-page login: join an existing multi-tab session instead of wiping it.
	// Modal login: never clear storage on mount (session expired UI must not nuke other tabs).
	useEffect(() => {
		if (isModal || loginModalOpen) {
			setDOMReady(true);
			return;
		}

		const stored = readStoredSession();
		if (stored && Util.isAccessTokenUsable(stored.access_token)) {
			setSession(stored);
			navigate(getDefaultPathForAccess({
				access: stored.access,
			}), { replace: true });
			return;
		}

		// Intentionally do NOT remove sess_info here — that used to desync other tabs.
		setDOMReady(true);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// If session arrives from another tab while this login page is open, leave.
	useEffect(() => {
		if (isModal) return;
		if (session?.access_token && Util.isAccessTokenUsable(session.access_token)) {
			navigate(getDefaultPathForAccess({ access }), { replace: true });
		}
	}, [session?.access_token, access, isModal, navigate]);

	useEffect(() => {
		setFormData((prev) => ({
			...prev,
			warning: !connected ? t("common:login.offline") : '',
		}));
	}, [connected, t]);

	return (
		<>
			{/* Form shell — app-wide live background is mounted in App.tsx */}
			<Box
				sx={{
					minHeight: isModal ? 'auto' : '100vh',
					width: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					p: isModal ? 0 : 2,
					position: 'relative',
					overflow: isModal ? 'visible' : 'hidden',
					background: 'transparent',
				}}
			>
				<Fade in={DOMReady} timeout={1000}>
					<Paper
						elevation={0}
						sx={{
							width: '100%',
							maxWidth: 440,
							p: { xs: 3, sm: 5 },
							borderRadius: 4,
							background: (t) =>
								t.palette.mode === 'dark'
									? 'rgba(30, 30, 30, 0.72)'
									: 'rgba(255, 255, 255, 0.78)',
							backdropFilter: 'blur(20px) saturate(1.35)',
							WebkitBackdropFilter: 'blur(20px) saturate(1.35)',
							border: (t) =>
								t.palette.mode === 'dark'
									? `1px solid ${alpha(t.palette.primary.main, 0.28)}`
									: `1px solid ${alpha(t.palette.primary.main, 0.14)}`,
							boxShadow: (t) =>
								t.palette.mode === 'dark'
									? `0 24px 64px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04), 0 0 80px ${alpha(t.palette.primary.main, 0.12)}`
									: `0 20px 56px rgba(0,0,0,0.12), 0 0 0 1px rgba(255,255,255,0.6), 0 0 60px ${alpha(t.palette.primary.main, 0.08)}`,
							position: 'relative',
							zIndex: 1,
						}}
					>
						<Stack spacing={3} alignItems="center">
							<Box textAlign="center">
								<Typography
									variant="h4"
									fontWeight={700}
									color="text.primary"
									sx={{ mb: 1 }}
								>
									{t("common:login.signIn")}
								</Typography>
								<Typography variant="body2" color="text.secondary">
									{t("common:login.subtitle")}
								</Typography>
							</Box>

							{formData.error && (
								<Alert severity="error" sx={{ width: '100%' }}>
									{formData.error}
								</Alert>
							)}
							{formData.success && (
								<Alert severity="success" sx={{ width: '100%' }}>
									{formData.success}
								</Alert>
							)}
							{formData.warning && (
								<Alert severity="warning" sx={{ width: '100%' }}>
									{formData.warning}
								</Alert>
							)}

							<Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
								<TextField
									required
									fullWidth
									label={t("common:ui.labels.username")}
									name="username"
									autoComplete="username"
									autoFocus
									value={formData.username}
									onChange={handleChange}
									disabled={loading}
									variant="outlined"
									sx={{
										mb: 2.5,
										'& .MuiOutlinedInput-root': {
											borderRadius: 2,
											backgroundColor: 'action.hover',
										},
									}}
								/>
								<TextField
									required
									fullWidth
									label={t("common:ui.labels.password")}
									name="password"
									type="password"
									autoComplete="current-password"
									value={formData.password}
									onChange={handleChange}
									disabled={loading}
									variant="outlined"
									sx={{
										mb: 3,
										'& .MuiOutlinedInput-root': {
											borderRadius: 2,
											backgroundColor: 'action.hover',
										},
									}}
								/>

								<Button
									type="submit"
									fullWidth
									variant="contained"
									size="large"
									disabled={loading || (!connected && !isModal)}
									sx={{
										py: 1.8,
										borderRadius: 3,
										textTransform: 'none',
										fontWeight: 600,
										fontSize: '1.05rem',
										background: (t) =>
											`linear-gradient(90deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
										boxShadow: '0 6px 20px rgba(99,102,241,0.4)',
										'&:hover': {
											boxShadow: '0 8px 24px rgba(99,102,241,0.5)',
											transform: 'translateY(-1px)',
										},
										transition: 'all 0.3s ease',
									}}
								>
									{loading ? (
										<CircularProgress size={26} color="inherit" />
									) : (
										t("common:login.signIn")
									)}
								</Button>
							</Box>

							<Typography variant="body2" color="text.secondary" align="center">
								<Link href="#" underline="hover" color="primary">
									{t("common:login.forgotPassword")}
								</Link>
							</Typography>

							<Typography
								variant="caption"
								color="text.disabled"
								sx={{ mt: 3, opacity: 0.7 }}
							>
								© {new Date().getFullYear()} <Link href="https://moleculesoft.net" target="_blank"
									rel="noopener noreferrer">MOLeCULE Soft</Link>. {t("common:login.securePrivate")}
							</Typography>
						</Stack>
					</Paper>
				</Fade>
			</Box>

			{DOMReady && !connected && !isModal && <OfflineScreen />}
		</>
	);
};

export default Login;
